﻿import { Box, Button, Checkbox, FormControl, FormLabel, Grid, Select, Stack, Switch, Text } from '@chakra-ui/react'
import type { PersonAdditionalData, PolicyHolderAdditionalData, SmartTravelInsuredPerson } from 'BrokerTsLib/src/dtos'
import { createSmartTravelPersonFromK1, defaultSmartTravelInsuredPerson, getSeparateInsuredPerson } from 'BrokerTsLib/src/helpers/smartTravel'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { subYears } from 'date-fns'

import { ConfirmRemoveButton } from '@/components/ConfirmRemoveButton'
import { InputGroup } from '@/components/InputGroup'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useCountries } from '@/hooks'

const name = 'property.additionalDataGcp.smartTravel.insuredPersons'

const defaultPerson = defaultSmartTravelInsuredPerson

export const SmartTravelInsuredPersonsForm = () => {
  const { control, watch, getValues, getFieldState, formState } = useFormContext()
  const { isDisabled: contracted } = useInsuranceParams()

  const { fields, append, update, remove } = useFieldArray({
    control,
    name
  })

  const persons = fields as unknown as (SmartTravelInsuredPerson & { id: string })[]

  const insuredCount = watch('property.additionalDataGcp.smartTravel.insuredCount') as number
  const isFamily = insuredCount == 3
  const maxPersons = isFamily ? 7 : (insuredCount ?? 1)
  const minPersons = isFamily ? 3 : (insuredCount ?? 1)

  const policyHolderPerson = getValues('policyHolder.person') as PersonAdditionalData | null
  const insuredPerson = getSeparateInsuredPerson(getValues('policyHolder') as PolicyHolderAdditionalData | null)
  const hasSeparateInsuredPerson = !!insuredPerson

  // pokud je pojištěný shodný s pojistníkem, nese osoba příznak isPolicyHolder (builder GČP s ní pak správně pracuje)
  const insuredChecked = persons.some(p => hasSeparateInsuredPerson ? p.isInsuredPerson : p.isPolicyHolder)
  const policyHolderChecked = persons.some(p => p.isPolicyHolder)

  // checkbox nemění počet řádků - zaškrtnutí předvyplní volný řádek údaji osoby z K1, odškrtnutí hodnoty smaže
  const handleToggle = (checked: boolean, source: PersonAdditionalData | null, flags: { isPolicyHolder: boolean, isInsuredPerson: boolean }) => {
    if (checked) {
      const freeIndex = persons.findIndex(p => !p.isPolicyHolder && !p.isInsuredPerson)
      const person = createSmartTravelPersonFromK1(source, flags)

      if (freeIndex >= 0) update(freeIndex, person)
      else if (persons.length < maxPersons) append(person)
    } else {
      const index = persons.findIndex(p => flags.isInsuredPerson ? p.isInsuredPerson : p.isPolicyHolder)
      if (index >= 0) update(index, { ...defaultPerson })
    }
  }

  const handleToggleInsured = (checked: boolean) =>
    handleToggle(checked,
      hasSeparateInsuredPerson ? insuredPerson : policyHolderPerson,
      { isPolicyHolder: !hasSeparateInsuredPerson, isInsuredPerson: hasSeparateInsuredPerson })

  const handleTogglePolicyHolder = (checked: boolean) =>
    handleToggle(checked, policyHolderPerson, { isPolicyHolder: true, isInsuredPerson: false })

  const { error } = getFieldState(name, formState)

  return (
    <FormBody>
      <Text fontSize='sm' color='gray.600'>
        {isFamily
          ? 'Chytré cestovko je sjednáno pro rodinu – zadejte 3 až 7 pojištěných osob, z toho maximálně 2 osoby starší 18 let.'
          : `Chytré cestovko je sjednáno pro ${maxPersons == 1 ? '1 pojištěnou osobu' : '2 pojištěné osoby'}.`
        }
      </Text>

      <Stack gap={2}>
        <Checkbox
          isChecked={insuredChecked}
          isDisabled={contracted}
          onChange={(e) => handleToggleInsured(e.target.checked)}
        >
          <Text as='span' fontWeight='normal'>
            Je <Text as='span' fontWeight='bold'>pojištěný</Text> zároveň pojištěnou osobou chytrého cestovka
          </Text>
        </Checkbox>

        {hasSeparateInsuredPerson &&
          <Checkbox
            isChecked={policyHolderChecked}
            isDisabled={contracted}
            onChange={(e) => handleTogglePolicyHolder(e.target.checked)}
          >
            <Text as='span' fontWeight='normal'>
              Je <Text as='span' fontWeight='bold'>pojistník</Text> zároveň pojištěnou osobou chytrého cestovka
            </Text>
          </Checkbox>
        }
      </Stack>

      <Stack gap={6}>
        {persons.map((person, index) => {
          const flagged = !!person.isPolicyHolder || !!person.isInsuredPerson

          return (
            <SmartTravelInsuredPersonRow
              key={person.id}
              index={index}
              readonly={contracted || flagged}
              canRemove={!contracted && !flagged && persons.length > minPersons}
              remove={remove}
            />
          )
        })}
      </Stack>

      {error?.message &&
        <Text fontSize='sm' color='red.500'>{error.message}</Text>
      }

      {!contracted && isFamily && persons.length < maxPersons &&
        <Box>
          <Button type='button' onClick={() => append({ ...defaultPerson })}>
            Přidat další osobu
          </Button>
        </Box>
      }
    </FormBody>
  )
}

type SmartTravelInsuredPersonRowProps = {
  index: number
  readonly: boolean
  canRemove: boolean
  remove: UseFieldArrayRemove
}

const SmartTravelInsuredPersonRow = ({
  index,
  readonly,
  canRemove,
  remove
}: SmartTravelInsuredPersonRowProps) => {
  const { register, setValue, watch, getFieldState, formState } = useFormContext()
  const { data: countries } = useCountries()

  const prefix = `${name}.${index}`

  const isForeigner = watch(`${prefix}.isForeigner`)

  const handleForeignerChange = (checked: boolean) => {
    setValue(`${prefix}.isForeigner`, checked)

    if (checked) {
      setValue(`${prefix}.birthnumber`, '')
    } else {
      setValue(`${prefix}.birthdate`, null)
      setValue(`${prefix}.nationalityFK`, null)
    }
  }

  const firstNameError = getFieldState(`${prefix}.firstName`, formState).error
  const lastNameError = getFieldState(`${prefix}.lastName`, formState).error
  const birthnumberError = getFieldState(`${prefix}.birthnumber`, formState).error
  const birthdateError = getFieldState(`${prefix}.birthdate`, formState).error
  const nationalityError = getFieldState(`${prefix}.nationalityFK`, formState).error

  const now = new Date()

  return (
    <Grid
      sx={{
        // pevná šablona + placeholder místo křížku, aby měly všechny řádky stejně široké sloupce
        gridTemplateColumns: { base: '1fr', md: '1fr 1fr 1.4fr 48px' },
        columnGap: 8,
        rowGap: 4,
        alignItems: 'start'
      }}
    >
      <FormControl isRequired={!readonly} isInvalid={!!firstNameError}>
        <FormLabel>Jméno</FormLabel>

        <InputGroup
          htmlSize={12}
          placeholder="Jan"
          isDisabled={readonly}
          errors={firstNameError}
          {...register(`${prefix}.firstName`)}
        />
      </FormControl>

      <FormControl isRequired={!readonly} isInvalid={!!lastNameError}>
        <FormLabel>Příjmení</FormLabel>

        <InputGroup
          htmlSize={12}
          placeholder="Novák"
          isDisabled={readonly}
          errors={lastNameError}
          {...register(`${prefix}.lastName`)}
        />
      </FormControl>

      <FormControl isInvalid={!!birthnumberError}>
        <FormLabel>Rodné číslo</FormLabel>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Switch
            isDisabled={readonly}
            isChecked={!!isForeigner}
            onChange={(e) => handleForeignerChange(e.target.checked)}
          />
          <FormLabel htmlFor={`${prefix}.isForeigner`} variant="switch" m={0}>
            Nemá
          </FormLabel>

          {!isForeigner &&
            <InputGroup
              htmlSize={12}
              ml={2}
              isDisabled={readonly}
              errors={birthnumberError}
              {...register(`${prefix}.birthnumber`, {
                setValueAs: (v: string) => (v ?? '').replace(/\D/g, '')
              })}
            />
          }
        </Box>
      </FormControl>

      {/* delší potvrzovací text se zalomí uvnitř tlačítka, aby nepřetékal mimo sloupec */}
      <Grid sx={{ alignSelf: 'start', mt: 9, justifyItems: 'start', '& button': { whiteSpace: 'normal', h: 'auto', minH: 10, py: 2 } }}>
        {canRemove &&
          <ConfirmRemoveButton
            hideCancelButton
            fullWidth={false}
            defaultLabel='X'
            onClick={() => remove(index)}
          />
        }
      </Grid>

      {isForeigner &&
        <>
          <FormControl isRequired={!readonly} isInvalid={!!nationalityError}>
            <FormLabel>Státní občanství</FormLabel>

            {countries?.data && countries.data.length > 0 &&
              <Select {...register(`${prefix}.nationalityFK`)} isDisabled={readonly}>
                <option disabled value=''>
                  Vyberte státní občanství
                </option>
                {countries.data.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            }
          </FormControl>

          <FormControl isRequired={!readonly} isInvalid={!!birthdateError}>
            <FormLabel>Datum narození</FormLabel>

            <Datepicker
              isDisabled={readonly}
              name={`${prefix}.birthdate`}
              htmlSize={12}
              options={{
                minDate: subYears(now, 120),
                maxDate: now
              }}
            />
          </FormControl>
        </>
      }
    </Grid>
  )
}
